// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
/* eslint-disable spire/export-styles */
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { xGoogleAnalyticsState } from "@werner/App/GoogleAnalyticsConnector/Store/Reducers";
import { getAuthenticatedHeaderSignInWidget } from "@werner/App/Store/Selectors/WidgetSelectors";
import { mapLinks, XMappedLink } from "@werner/Overrides/Widgets/Common/HeaderSignIn";
import { myAccountLinkStyles } from "@werner/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth",
    nodeId = "nodeId",
    leftMargin = "leftMargin",
    marginBottom = "marginBottom",
    customCSS = "customCSS",
    // XNG-Changes
    title = "title",
    extrLinks = "extrLinks",
    sourceFromMiniAccount = "sourceFromMiniAccount",
}

interface MyAccountExtraLinks {
    fields: {
        destination: LinkFieldValue;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.leftMargin]: number;
        [fields.marginBottom]: number;
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.title]: string;
        [fields.extrLinks]: MyAccountExtraLinks[];
        [fields.sourceFromMiniAccount]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    // </XNG-Changes>
    const miniLinks = getAuthenticatedHeaderSignInWidget(state);
    let linkList: XMappedLink[] = [];
    if (miniLinks) {
        linkList = mapLinks(state, miniLinks?.extraLinks);
    }
    // </XNG-Changes>
    return {
        // </XNG-Changes>
        miniMyAccountFields: linkList,
        // </XNG-Changes>
        rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        affiliation: (state as xGoogleAnalyticsState).xGoogleAnalytics.affiliation,
    };
};

interface ListStyleProp {
    leftMargin: number;
    marginBottom: number;
}

const ListStyle = styled.ul<ListStyleProp>`
    margin-left: ${props => props.leftMargin}px;

    li {
        margin-bottom: ${props => props.marginBottom}px;
    }
`;

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const NavigationList = (props: Props) => {
    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            h5 {
                margin-bottom: 12px;
            }
            ${props.fields.customCSS}
        `,
    };

    // </XNG-Changes>
    const renderMappedLink = (pageLink: XMappedLink, currentDepth: number) => {
        return (
            <li key={pageLink.url} className="navigation-list-item">
                <Link
                    className="navigation-list-link"
                    data-test-selector={`navigationList-${pageLink.title}`}
                    href={pageLink.url}
                    {...myAccountLinkStyles}
                    aria-label={`Navigation-List-${pageLink.title}`}
                    onClick={(window as any)._GaMyAccountLinksClick(pageLink)}
                >
                    {pageLink.title}
                </Link>
            </li>
        );
    };

    // </XNG-Changes>
    const renderLink = (pageLink: XMappedLink | PageLinkModel, currentDepth: number, fromSource: boolean) => {
        const mappedLink = pageLink as XMappedLink;
        const pageLinkData = pageLink as PageLinkModel;
        if (currentDepth > props.fields.depth) {
            return;
        }

        if (
            pageLinkData.excludeFromNavigation ||
            (pageLinkData.type === "ChangeCustomerPage" && !props.displayChangeCustomerLink)
        ) {
            return;
        }

        if (pageLinkData.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;

        return fromSource ? (
            <li key={pageLinkData.id} className="navigation-list-item">
                <Link
                    className="navigation-list-link"
                    data-test-selector={`navigationList-${pageLinkData.title}`}
                    href={pageLinkData.url}
                    {...myAccountLinkStyles}
                    onClick={() => (window as any)._GaMyAccountLinksClick(pageLinkData, props.affiliation)}
                >
                    {pageLinkData.title}
                </Link>
                {pageLinkData.children && pageLinkData.children.length > 0 && (
                    <ListStyle
                        marginBottom={props.fields.marginBottom}
                        leftMargin={props.fields.leftMargin}
                        key={pageLinkData.id}
                    >
                        {pageLinkData.children.map(link => renderLink(link, nextDepth, fromSource))}
                    </ListStyle>
                )}
            </li>
        ) : (
            renderMappedLink(mappedLink, 1)
        );
    };

    // <XNG-Changes>
    const currentMode = getCookie(NavigationModeCookieName);
    let filteredRootPageLinks = props.rootPageLink?.children.filter(link => !link.excludeFromNavigation);
    if (!currentMode || currentMode === "Storefront") {
        filteredRootPageLinks = filteredRootPageLinks?.filter(pageLink => pageLink.type !== "VmiUsersPage");
    }
    const extraLinks = useGetLinks(props.fields.extrLinks, o => o.fields.destination);
    const links = props.fields.sourceFromMiniAccount
        ? props.miniMyAccountFields.filter(
              link => filteredRootPageLinks?.some(m => m.url === link.url) || link.billTrust,
          ) || []
        : props.rootPageLink?.children;

    // </XNG-Changes>

    return (
        <StyledWrapper {...customCssWrapper}>
            {/* <XNG-Changes> */}
            <Typography variant="h5">{props.fields.title}</Typography>
            {/* </XNG-Changes> */}
            <ListStyle
                className="navigation-list"
                marginBottom={props.fields.marginBottom}
                leftMargin={props.fields.leftMargin}
                data-test-selector="navigationList"
            >
                {links.map(link => renderLink(link, 1, props.fields.sourceFromMiniAccount))}
                {extraLinks.links.map(link => (
                    <li key={link.url} className="navigation-list-item">
                        <Link
                            className="navigation-list-link"
                            data-test-selector={`navigationList-${link.title}`}
                            href={link.url}
                            {...myAccountLinkStyles}
                            aria-label={`${link.title}`}
                        >
                            {link.title}
                        </Link>
                    </li>
                ))}
            </ListStyle>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.navigation-list{
}

.navigation-list-item {
}

.navigation-list-link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavigationList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product & Order Management",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.leftMargin,
                displayName: "Left Margin",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.marginBottom,
                displayName: "Margin Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 100,
                min: 0,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },

            // XNG-Changes
            {
                name: fields.sourceFromMiniAccount,
                displayName: "Get Source From mini My Account",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
            {
                name: fields.extrLinks,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        text,
                    } = item.fields;

                    if (text) {
                        return text;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        displayName: "link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
